import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { getSocket } from "../services/chatSocket";

const API_URL = process.env.REACT_APP_API_URL;

const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.id || payload._id || payload.sub || null;
  } catch {
    return null;
  }
};

// requestIds: string[] — tüm ilgili request ID'leri
// activeRequestId: string — yeni mesaj gönderilecek (en yeni) room
const useChat = (requestIds = [], activeRequestId) => {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const requestIdsKey = requestIds.join(",");
  const prevIdsRef = useRef("");

  const fetchAllHistory = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem("token");
      const results = await Promise.all(
        ids.map((id) =>
          axios
            .get(`${API_URL}/chat/requests/${id}/messages?page=1&limit=100`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
              const data =
                res.data?.items ||
                res.data?.data ||
                res.data?.messages ||
                (Array.isArray(res.data) ? res.data : []);
              return Array.isArray(data) ? data : [];
            })
            .catch(() => [])
        )
      );
      // Tüm mesajları birleştir, tarihe göre sırala, duplicate temizle
      const merged = results.flat();
      const unique = Array.from(new Map(merged.map((m) => [m._id || Math.random(), m])).values());
      unique.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setMessages(unique);
    } catch (err) {
      console.error("[Chat] fetchAllHistory error:", err.message);
      setMessages([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Socket event listeners
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onMessage = (msg) => {
      const msgRequestId =
        msg.requestId || msg.request?._id || msg.request || msg.roomId || msg.room;
      // İlgili room'lardan biri ise ekle
      if (msgRequestId && !requestIds.includes(String(msgRequestId))) return;
      setMessages((prev) => {
        const withoutOptimistic = prev.filter(
          (m) => !(m._optimistic && m.content === msg.content && m.senderId === msg.senderId)
        );
        if (withoutOptimistic.some((m) => m._id === msg._id)) return withoutOptimistic;
        return [...withoutOptimistic, msg];
      });
    };
    const onRead = ({ messageIds }) => {
      if (!Array.isArray(messageIds)) return;
      setMessages((prev) =>
        prev.map((m) => (messageIds.includes(m._id) ? { ...m, isRead: true } : m))
      );
    };

    if (socket.connected) setConnected(true);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("message:new", onMessage);
    socket.on("message", onMessage);
    socket.on("new-message", onMessage);
    socket.on("chat:message", onMessage);
    socket.on("messages:read", onRead);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("message:new", onMessage);
      socket.off("message", onMessage);
      socket.off("new-message", onMessage);
      socket.off("chat:message", onMessage);
      socket.off("messages:read", onRead);
    };
  }, [requestIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ID listesi değişince geçmişi yeniden çek ve tüm room'lara join ol
  useEffect(() => {
    if (!requestIds.length) return;
    if (requestIdsKey === prevIdsRef.current) return;
    prevIdsRef.current = requestIdsKey;

    setMessages([]);
    fetchAllHistory(requestIds);

    const socket = getSocket();
    if (!socket) return;

    const joinAll = () => {
      requestIds.forEach((id) => {
        socket.emit("join", { requestId: id });
      });
    };

    if (socket.connected) {
      joinAll();
    } else {
      socket.once("connect", joinAll);
    }
  }, [requestIdsKey, fetchAllHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Yeni mesajlar aktif (en yeni) room'a gönderilir
  const sendMessage = useCallback(
    (content) => {
      if (!activeRequestId || !content.trim()) return;
      const myId = getCurrentUserId();
      const optimistic = {
        _id: `optimistic_${Date.now()}`,
        _optimistic: true,
        requestId: activeRequestId,
        senderId: myId,
        content,
        messageType: "text",
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      setMessages((prev) => [...prev, optimistic]);
      getSocket()?.emit("sendMessage", { requestId: activeRequestId, content, messageType: "text" });
    },
    [activeRequestId]
  );

  const sendFile = useCallback(
    ({ attachmentUrl, fileName, mimeType, messageType }) => {
      if (!activeRequestId || !attachmentUrl) return;
      const myId = getCurrentUserId();
      const optimistic = {
        _id: `optimistic_${Date.now()}`,
        _optimistic: true,
        requestId: activeRequestId,
        senderId: myId,
        content: "",
        messageType: messageType || "file",
        attachmentUrl,
        fileName,
        mimeType,
        createdAt: new Date().toISOString(),
        isRead: false,
      };
      setMessages((prev) => [...prev, optimistic]);
      getSocket()?.emit("sendMessage", {
        requestId: activeRequestId,
        content: "",
        messageType: messageType || "file",
        attachmentUrl,
        fileName,
        mimeType,
      });
    },
    [activeRequestId]
  );

  const markAsRead = useCallback(() => {
    if (!activeRequestId) return;
    getSocket()?.emit("markAsRead", { requestId: activeRequestId });
  }, [activeRequestId]);

  return { messages, connected, historyLoading, sendMessage, sendFile, markAsRead };
};

export default useChat;
